import { useEffect, useRef, useState } from 'react';
import { NavLink, Route, Routes, Navigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AdminDashboard } from './AdminDashboard';
import { AdminItems } from './AdminItems';
import { AdminItemEdit } from './AdminItemEdit';
import { AdminBrands } from './AdminBrands';
import { AdminCategories } from './AdminCategories';
import { AdminOccasions } from './AdminOccasions';
import { AdminOrders } from './AdminOrders';
import { AdminOrderDetail } from './AdminOrderDetail';
import { AdminCreditPurchases } from './AdminCreditPurchases';
import { AdminCustomers } from './AdminCustomers';
import { AdminCustomerDetail } from './AdminCustomerDetail';
import { AdminTeam } from './AdminTeam';
import { AdminCredits } from './AdminCredits';
import { AdminDiscountCodes } from './AdminDiscountCodes';
import { AdminSiteContent } from './AdminSiteContent';
import { AdminReturns } from './AdminReturns';
import { AdminReturnedItems } from './AdminReturnedItems';
import { AdminEmailLog } from './AdminEmailLog';
import { AdminIdVerifications } from './AdminIdVerifications';

/**
 * Admin-area shell. Top-nav layout (distinct from the storefront's phone-frame
 * feel) optimised for desktop CMS workflows. Mobile collapses gracefully.
 */
export function AdminShell() {
  const { profile, signOut } = useAuth();
  const location = useLocation();
  const initial = (profile?.first_name ?? profile?.email ?? '?').charAt(0).toUpperCase();
  const [variablesOpen, setVariablesOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const variablesRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  // The storefront body has `overflow:hidden; height:100vh` for its phone-app
  // feel. The admin needs normal page scroll, so we add a class to <body>
  // while AdminShell is mounted that overrides those rules.
  useEffect(() => {
    document.body.classList.add('admin-body');
    return () => {
      document.body.classList.remove('admin-body');
    };
  }, []);

  // Close dropdowns on outside click + on route change.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      const t = e.target as Node;
      if (variablesRef.current && !variablesRef.current.contains(t)) {
        setVariablesOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(t)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);
  useEffect(() => {
    setVariablesOpen(false);
    setProfileOpen(false);
    setMobileNavOpen(false);
  }, [location.pathname]);

  const variablesActive = ['brands', 'categories', 'occasions'].some((p) =>
    location.pathname.includes(`/admin/${p}`)
  );

  return (
    <div className="admin-app">
      <header className="admin-topbar">
        <button
          className="admin-mobile-toggle"
          onClick={() => setMobileNavOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
        </button>
        <Link to="/admin" className="brand">
          LABEL ROTATION
          <span className="pill">CMS</span>
        </Link>
        <nav className={mobileNavOpen ? 'open' : undefined}>
          <NavItem to=".">Dashboard</NavItem>
          <NavItem to="items">Items</NavItem>
          <NavItem to="orders">Orders</NavItem>
          <NavItem to="returns">Returns</NavItem>
          <NavItem to="returned-items">Returned Items</NavItem>
          <NavItem to="credit-purchases">Credit Sales</NavItem>
          <NavItem to="customers">Customers</NavItem>
          <div className="nav-dropdown" ref={variablesRef}>
            <button
              className={`nav-link${variablesActive ? ' active' : ''}`}
              onClick={() => setVariablesOpen((v) => !v)}
              aria-haspopup="true"
              aria-expanded={variablesOpen}
              type="button"
            >
              Variables
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ marginLeft: 4 }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {variablesOpen && (
              <div className="nav-dropdown-menu">
                <Link to="brands" className="nav-dropdown-item">
                  Brands
                  <span className="hint">Manage clothing labels</span>
                </Link>
                <Link to="categories" className="nav-dropdown-item">
                  Categories
                  <span className="hint">Outerwear, shirts, etc.</span>
                </Link>
                <Link to="occasions" className="nav-dropdown-item">
                  Occasions
                  <span className="hint">Wedding, smart-casual, etc.</span>
                </Link>
              </div>
            )}
          </div>
          <NavItem to="discount-codes">Discounts</NavItem>
          <NavItem to="site-content">Site Content</NavItem>
          <NavItem to="email-log">Email Log</NavItem>
          {profile?.role === 'super_admin' && <NavItem to="team">Team</NavItem>}
        </nav>
        <div className="right">
          <div className="profile-dropdown" ref={profileRef}>
            <button
              type="button"
              className="user"
              onClick={() => setProfileOpen((v) => !v)}
              aria-haspopup="true"
              aria-expanded={profileOpen}
            >
              <span className="avatar">{initial}</span>
              <span className="user-name">
                {profile?.first_name ?? profile?.email?.split('@')[0]}
              </span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {profileOpen && (
              <div className="profile-menu">
                <div className="profile-info">
                  <div className="profile-name">
                    {[profile?.first_name, profile?.last_name].filter(Boolean).join(' ') ||
                      profile?.email?.split('@')[0]}
                  </div>
                  <div className="profile-email">{profile?.email}</div>
                  <div className="profile-role">
                    {profile?.role?.replace('_', ' ')}
                  </div>
                </div>
                <Link to="/" className="profile-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                  View site
                </Link>
                <Link to="/account" className="profile-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 21a8 8 0 0116 0" />
                  </svg>
                  My rotation
                </Link>
                <button
                  type="button"
                  onClick={() => void signOut()}
                  className="profile-item danger"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="admin-main">
        <Routes>
          <Route index element={<AdminDashboard />} />

          <Route path="items" element={<AdminItems />} />
          <Route path="items/new" element={<AdminItemEdit />} />
          <Route path="items/:id" element={<AdminItemEdit />} />

          <Route path="brands" element={<AdminBrands />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="occasions" element={<AdminOccasions />} />

          <Route path="orders" element={<AdminOrders />} />
          <Route path="orders/:id" element={<AdminOrderDetail />} />
          <Route path="returns" element={<AdminReturns />} />
          <Route path="returned-items" element={<AdminReturnedItems />} />
          <Route path="credit-purchases" element={<AdminCreditPurchases />} />

          <Route path="customers" element={<AdminCustomers />} />
          <Route path="customers/:id" element={<AdminCustomerDetail />} />
          <Route path="id-verifications" element={<AdminIdVerifications />} />

          <Route path="credits" element={<AdminCredits />} />
          <Route path="discount-codes" element={<AdminDiscountCodes />} />
          <Route path="site-content" element={<AdminSiteContent />} />
          <Route path="email-log" element={<AdminEmailLog />} />
          <Route path="team" element={<AdminTeam />} />

          <Route path="*" element={<Navigate to="." replace />} />
        </Routes>
      </main>
    </div>
  );
}

function NavItem({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === '.'}
      className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
    >
      {children}
    </NavLink>
  );
}
